// src/components/AuthStatus.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { user as userApi } from '../services/api.ts';

const boxStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 12,
  right: 12,
  background: 'rgba(16,185,129,0.95)', // emerald-500
  color: 'white',
  padding: '8px 12px',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  fontSize: 12,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const buttonStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.2)',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  padding: '4px 8px',
  cursor: 'pointer',
  fontSize: 12,
};

const AuthStatus: React.FC = () => {
  const { session, signOut } = useAuth();
  const history = useHistory();
  const [visible, setVisible] = useState(false);
  const [msg, setMsg] = useState('');
  const lastTokenRef = useRef<string | null>(null);
  const redirectingRef = useRef(false);

  useEffect(() => {
    let timeout: number | undefined;
    const run = async () => {
      const token = session?.access_token ?? null;
      if (!token) return;
      // Avoid duplicate calls (e.g., React StrictMode double-effect)
      if (lastTokenRef.current === token) return;
      lastTokenRef.current = token;
      try {
        const profile = await userApi.getProfile();
        const email = profile?.email || session?.user?.email || 'signed in';
        setMsg(`Signed in as ${email}`);
        setVisible(true);
        timeout = window.setTimeout(() => setVisible(false), 2500);
      } catch (err: any) {
        const status = err?.response?.status;
        if ((status === 401 || status === 403) && !redirectingRef.current) {
          redirectingRef.current = true;
          try {
            await signOut();
          } finally {
            history.replace('/login');
            // Allow future redirects after navigation completes
            setTimeout(() => {
              redirectingRef.current = false;
            }, 1000);
          }
        } else {
          setMsg('Auth check failed');
          setVisible(true);
          timeout = window.setTimeout(() => setVisible(false), 2500);
        }
      }
    };
    run();
    return () => {
      if (timeout) window.clearTimeout(timeout);
    };
  }, [session, signOut, history]);

  if (!visible) return null;
  return (
    <div style={boxStyle} role="status" aria-live="polite">
      <span>{msg}</span>
      <button style={buttonStyle} onClick={() => setVisible(false)}>Hide</button>
      <button style={buttonStyle} onClick={async () => { await signOut(); history.replace('/login'); }}>Sign out</button>
      {session?.access_token && (
        <button
          style={buttonStyle}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(session.access_token!);
              setMsg('JWT copied');
            } catch {
              setMsg('Copy failed');
            }
          }}
        >Copy JWT</button>
      )}
    </div>
  );
};

export default AuthStatus;
